import ThreeStepAsyncOpDialog from "../ThreeStepAsyncOpDialog/ThreeStepAsyncOpDialog";
import {MC_Device} from "../../../iot/MC_Device";
import {ThreeStepAddDialogBaseProps} from "./ThreeStepAddDialogBaseProps";
import {DevicesContext} from "../../../../App";
import {MC_Backend} from "../../../MC_Backend";
import DeviceItem from "../../item-list/DeviceItem";
import PaginatedSelectItemList from "../../item-list/select-list/PaginatedSelectItemList";
import {MC_Template_Role} from "../../../iot/MC_Parent_Template";
import {MC_Constants} from "../../../MC_Constants";

export interface AddChildDeviceToRoleDialogProps extends ThreeStepAddDialogBaseProps<MC_Device> {
    parent: MC_Device;
    role: MC_Template_Role | null;
}
function AddChildDeviceToRoleDialog(props: AddChildDeviceToRoleDialogProps) {
    // Don't show dialog unless a role is selected
    if (props.role == null) {
        return null;
    }
    const parent = props.parent;
    const role = props.role;
    // Helper fns
    const getSelectionComponent = (selectFn: (device: MC_Device) => void) => {
        return (
            <DevicesContext.Consumer>
                {(devicesCtx) => {
                    let loadFn = () => MC_Backend.getInstance().loadDevices().finally();
                    let renderItem = (idx: number, d: MC_Device | null) => {
                        // The device cannot be selected if:
                        // 1) The device already has a parent
                        // 2) The device type doesn't match the role's device type
                        let deviceSelectFn = undefined;
                        if (d != null && d.parentDeviceID == null) {
                            const roleDType: string = role.deviceType;
                            if (roleDType === d.type || roleDType === MC_Constants.ROLE_DEVICE_TYPE_ANY) {
                                deviceSelectFn = selectFn;
                            }
                        }
                        const key = (d != null) ? d.id : "null" + idx;
                        return <DeviceItem key={key} device={d} selectAction={deviceSelectFn} nameLink={false}/>;
                    };
                    const sortByName = (a: MC_Device, b: MC_Device) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                    const sortFn = (d1: MC_Device, d2: MC_Device) => {
                        // Sort devices of the relevant type first (if applicable)
                        if (role.deviceType !== MC_Constants.ROLE_DEVICE_TYPE_ANY) {
                            const d1TypeMatch: boolean = (d1.type === role.deviceType);
                            const d2TypeMatch: boolean = (d2.type === role.deviceType);
                            if (d1TypeMatch !== d2TypeMatch) {
                                // One device matches the type
                                return (d1TypeMatch) ? -1 : 1;
                            }
                        }
                        // Sort devices without a parent first
                        const d1HasParent: boolean = (d1.parentDeviceID != null);
                        const d2HasParent: boolean = (d2.parentDeviceID != null);
                        // If these devices don't share the same parental state, compare them
                        if (d1HasParent !== d2HasParent) {
                            // Sort relevant devices first (no parent = sort first)
                            return (d1HasParent) ? 1 : -1;
                        }
                        // Default to sort by name
                        return sortByName(d1, d2);
                    }
                    return <PaginatedSelectItemList
                        allowSearch={true}
                        pageSize={4}
                        fillPageWithNullItems={true}
                        loadDataFn={loadFn}
                        emptyMessage={"No devices found."}
                        sortFn={sortFn}
                        dataArray={devicesCtx.devices}
                        loading={devicesCtx.loading}
                        errMsg={devicesCtx.errMsg}
                        renderItem={renderItem}
                        isDevices={true}
                    />
                }}
            </DevicesContext.Consumer>
        );
    }
    const getConfirmDescFn = (d: MC_Device) => {
        return "You're about to assign '" + d.name
            + "' to the role '" + role.name
            + "' for parent '" + parent.name + "'.";
    }
    const assignDeviceFn = async (d: MC_Device) => {
        await MC_Backend.getInstance().updateChildDeviceRelation(
            d, parent,
            role.id, "add"
        );
        return d;
    };
    const getSuccessDesc = (d: MC_Device) => {
        return "Device '" + d.name + "' has been assigned.";
    };
    // UI
    return (
        <ThreeStepAsyncOpDialog<MC_Device>
            open={props.open}
            closeDialogFn={props.closeDialogFn}
            title={"Assign Device To Role"}
            getSelectionComponent={getSelectionComponent}
            getConfirmDesc={getConfirmDescFn}
            confirmText={"Assign"}
            performActionFn={assignDeviceFn}
            getSuccessDesc={getSuccessDesc}
            doneSuccessFn={() => {}}
            doneFailureFn={() => {}}
        />
    );
}

export default AddChildDeviceToRoleDialog;